export const FINANCIAL_AID_ALLOWED_UPLOAD_FILE_TYPES = new Set(['application/pdf', 'image/jpeg', 'image/png']);
export const MAX_FINANCIAL_AID_UPLOAD_FILE_SIZE_BYTES = 10 * 1024 * 1024;

const GENERIC_BROWSER_FILE_TYPES = new Set([
  '',
  'application/octet-stream',
  'binary/octet-stream',
  'application/download',
  'application/force-download',
  'application/x-download',
]);

const PDF_FILE_TYPES = new Set([
  'application/pdf',
  'application/acrobat',
  'application/nappdf',
  'application/x-pdf',
  'application/vnd.pdf',
  'text/pdf',
  'text/x-pdf',
]);

const JPEG_FILE_TYPES = new Set(['image/jpeg', 'image/jpg', 'image/pjpeg']);
const PNG_FILE_TYPES = new Set(['image/png', 'image/x-png']);

const normalizeMimeType = (fileType: unknown): string =>
  String(fileType ?? '').split(';')[0]?.trim().toLowerCase() ?? '';

const fileNameHasExtension = (fileName: unknown, extensions: string[]): boolean => {
  const normalizedName = String(fileName ?? '').trim().toLowerCase();
  return extensions.some((extension) => normalizedName.endsWith(extension));
};

export type FinancialAidUploadCandidate = {
  name?: string;
  type?: string;
  size?: number;
};

export type FinancialAidUploadHeaderSource = {
  uploadHeaders?: Record<string, string>;
};

export const inferFinancialAidUploadFileType = (file: FinancialAidUploadCandidate): string => {
  const reportedType = normalizeMimeType(file.type);

  if (fileNameHasExtension(file.name, ['.pdf']) && (PDF_FILE_TYPES.has(reportedType) || GENERIC_BROWSER_FILE_TYPES.has(reportedType))) {
    return 'application/pdf';
  }
  if (fileNameHasExtension(file.name, ['.jpg', '.jpeg']) && (JPEG_FILE_TYPES.has(reportedType) || GENERIC_BROWSER_FILE_TYPES.has(reportedType))) {
    return 'image/jpeg';
  }
  if (fileNameHasExtension(file.name, ['.png']) && (PNG_FILE_TYPES.has(reportedType) || GENERIC_BROWSER_FILE_TYPES.has(reportedType))) {
    return 'image/png';
  }
  if (PDF_FILE_TYPES.has(reportedType)) return 'application/pdf';
  if (JPEG_FILE_TYPES.has(reportedType)) return 'image/jpeg';
  if (PNG_FILE_TYPES.has(reportedType)) return 'image/png';
  return reportedType || 'application/octet-stream';
};

export const getFinancialAidUploadFileValidationError = (file: FinancialAidUploadCandidate): string | null => {
  const fileType = inferFinancialAidUploadFileType(file);
  if (!FINANCIAL_AID_ALLOWED_UPLOAD_FILE_TYPES.has(fileType)) {
    return 'Please upload a PDF, JPG, or PNG file.';
  }
  if (typeof file.size !== 'number' || file.size <= 0) {
    return 'Please upload a non-empty PDF, JPG, or PNG file.';
  }
  if (file.size > MAX_FINANCIAL_AID_UPLOAD_FILE_SIZE_BYTES) {
    return 'Please upload a file smaller than 10MB.';
  }
  return null;
};

export const readFinancialAidUploadHeaders = (
  uploadInfo: FinancialAidUploadHeaderSource,
  fileType: string,
): Record<string, string> => {
  const headers = { ...(uploadInfo.uploadHeaders ?? {}) };
  const existingContentTypeKey = Object.keys(headers).find((key) => key.toLowerCase() === 'content-type');
  const contentType = existingContentTypeKey ? headers[existingContentTypeKey] : fileType;
  if (existingContentTypeKey && existingContentTypeKey !== 'Content-Type') {
    delete headers[existingContentTypeKey];
  }
  return {
    ...headers,
    'Content-Type': contentType || fileType,
  };
};
